import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  BackButton,
  FormField,
  FormGrid,
  FormSection,
  InlineFeedback,
  PhoneField,
  PrimaryButton,
  RutField,
} from 'components/forms';
import TemplateComposer, { type TemplateComposerHandle } from 'components/templates/TemplateComposer';
import { NAME_PARAM_KEYS, type Template, USER_PARAM_KEYS } from 'components/templates/types';
import { useFeedback } from 'contexts/FeedbackContext';
import useCurrentUser from 'hooks/useCurrentUser';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useCreateManualLeadMutation } from 'services/api/clients.api';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';
import capitalize from 'utils/capitalize';
import { SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID } from 'utils/constants';
import validateRut from 'utils/validations/validateRut';

const glassPaperSx = {
  backdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  boxShadow: SHADOW,
  borderRadius: RADIUS_CARD,
  border: `1px solid ${alpha(NAVY, 0.06)}`,
} as const;

const glassBackdropSx = {
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(NAVY, 0.25),
} as const;

const toggleGroupSx = {
  '& .MuiToggleButton-root': {
    flex: 1,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.9375rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    px: 2,
    py: 1.15,
    borderRadius: RADIUS_COIN,
    border: 'none',
    color: alpha(NAVY, 0.55),
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    '&.Mui-selected': {
      backgroundColor: NAVY,
      color: '#fff',
      '&:hover': { backgroundColor: alpha(NAVY, 0.87) },
    },
    '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
  },
} as const;

interface Props {
  open: boolean;
  onClose: () => void;
  businessUnitId: number;
  onSuccess: () => void;
}

const NewLeadModal: FC<Props> = ({ open, onClose, businessUnitId, onSuccess }) => {
  const currentUser = useCurrentUser();
  const { notifySaved } = useFeedback();
  const [createManualLead, { isLoading: isCreating }] = useCreateManualLeadMutation();
  const composerRef = useRef<TemplateComposerHandle>(null);
  // The reservation number and AI-off default belong to the airport flow (Salfa RAC Aeropuerto, BU 188);
  // tie them to the target business unit so admins creating the lead capture them too.
  const isRacAeropuerto = businessUnitId === SALFA_RAC_AEROPUERTO_BUSINESS_UNIT_ID;

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [rut, setRut] = useState('');
  const [email, setEmail] = useState('');
  const [reservationNumber, setReservationNumber] = useState('');
  const [isBusinessHours, setIsBusinessHours] = useState<boolean | null>(null);
  const [templateCanSend, setTemplateCanSend] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setLastName('');
    setPhone('');
    setRut('');
    setEmail('');
    setReservationNumber('');
    setIsBusinessHours(null);
    setTemplateCanSend(false);
    setSubmitFailed(false);
    setSubmitErrorMsg(null);
  };

  const prefill = useCallback(
    (paramName: string): string => {
      if (NAME_PARAM_KEYS.includes(paramName)) return capitalize(name) || '';
      if (USER_PARAM_KEYS.includes(paramName)) return capitalize(currentUser?.name) || '';
      return '';
    },
    [name, currentUser?.name]
  );

  const templateFilter = useCallback(
    (template: Template): boolean => {
      if (!['approved', 'reinstated'].includes(template.status?.toLowerCase())) return false;
      if (currentUser?.allowAllTemplates) return true;
      return (currentUser?.allowedTemplateNames ?? []).includes(template.name);
    },
    [currentUser?.allowAllTemplates, currentUser?.allowedTemplateNames]
  );

  const rutIsValid = useMemo(() => !rut || validateRut(rut), [rut]);
  const missingFields = useMemo(() => {
    const list: string[] = [];
    if (!name.trim()) list.push('Nombre');
    if (phone.replace(/\D/g, '').length < 8) list.push('Teléfono');
    if (rut && !rutIsValid) list.push('RUT');
    if (isRacAeropuerto && !reservationNumber.trim()) list.push('Número de Reserva');
    if (isRacAeropuerto && isBusinessHours === null) list.push('Horario');
    return list;
  }, [name, phone, rut, rutIsValid, isRacAeropuerto, reservationNumber, isBusinessHours]);

  const canSubmit = missingFields.length === 0 && templateCanSend && !isCreating;

  const handleSubmit = () => {
    setSubmitErrorMsg(null);
    const data = composerRef.current?.getSubmitData();
    if (missingFields.length > 0 || !data || !templateCanSend || isBusinessHours === null) {
      setSubmitFailed(true);
      return;
    }

    createManualLead({
      businessUnitId,
      phone,
      name: name.trim(),
      lastName: lastName.trim() || undefined,
      rut,
      email: email.trim() || undefined,
      reservationNumber: reservationNumber.trim() || undefined,
      isBusinessHours,
      template: {
        templateName: data.templateName,
        components: data.components,
        languageCode: 'es',
        aiStatus: data.aiStatus,
        postAt: data.postAt ?? undefined,
      },
    })
      .unwrap()
      .then((res) => {
        notifySaved(
          res.scheduled
            ? 'Lead creado, plantilla programada'
            : res.messageSent
              ? 'Lead creado y plantilla enviada'
              : 'Lead creado'
        );
        onSuccess();
        resetForm();
        onClose();
      })
      .catch((error) => {
        console.error('[NewLeadModal] Error creating manual lead:', error);
        setSubmitErrorMsg('No pudimos crear el lead. Inténtalo nuevamente en unos segundos.');
      });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
    >
      <DialogTitle sx={{ color: NAVY, fontWeight: 700, fontFamily: 'Jost, sans-serif', fontSize: '1.25rem' }}>
        Nueva Conversación
      </DialogTitle>
      <DialogContent sx={{ fontFamily: 'Jost, sans-serif' }}>
        {submitFailed && missingFields.length > 0 && (
          <InlineFeedback severity="error" sx={{ mb: 1, mt: 1 }}>
            {`Faltan datos: ${missingFields.join(', ')}.`} Revisa los campos marcados.
          </InlineFeedback>
        )}
        {submitFailed && missingFields.length === 0 && !templateCanSend && (
          <InlineFeedback severity="error" sx={{ mb: 1, mt: 1 }}>
            Selecciona una plantilla y completa todas sus variables.
          </InlineFeedback>
        )}
        {submitErrorMsg && (
          <InlineFeedback severity="error" sx={{ mb: 1, mt: 1 }}>
            {submitErrorMsg}
          </InlineFeedback>
        )}

        <FormSection eyebrow="Contacto" title="¿Quién es el lead?" ghost dense>
          <FormGrid columns={2} gap={2}>
            <FormField
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={submitFailed && !name.trim() ? 'El nombre es obligatorio' : undefined}
              placeholder="Ej: María Pérez"
              required
            />
            <FormField
              label="Apellido"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Ej: Pérez"
              optional
            />
            <PhoneField
              label="Teléfono"
              value={phone}
              onChange={setPhone}
              error={submitFailed && phone.replace(/\D/g, '').length < 8 ? 'Ingresa un teléfono válido' : undefined}
              placeholder="+56 9 1234 5678"
              required
            />
            <RutField
              label="RUT"
              value={rut}
              onChange={setRut}
              error={submitFailed && rut && !rutIsValid ? 'El RUT no es válido' : undefined}
              success={!!rut && rutIsValid}
              optional
            />
            <FormField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              optional
            />
            {isRacAeropuerto && (
              <>
                <FormField
                  label="Número de Reserva"
                  value={reservationNumber}
                  onChange={(e) => setReservationNumber(e.target.value)}
                  error={submitFailed && !reservationNumber.trim() ? 'El número de reserva es obligatorio' : undefined}
                  placeholder="Ej: ABC-12345"
                  required
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: alpha(NAVY, 0.7),
                      mb: 0.875,
                      letterSpacing: '0.005em',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    Horario
                    <Typography
                      component="span"
                      sx={{ color: '#E7233B', fontFamily: 'Jost, sans-serif', fontWeight: 600, ml: 0.25 }}
                      aria-hidden
                    >
                      *
                    </Typography>
                  </Typography>
                  <ToggleButtonGroup
                    value={isBusinessHours}
                    exclusive
                    onChange={(_, value: boolean | null) => setIsBusinessHours(value)}
                    fullWidth
                    sx={toggleGroupSx}
                  >
                    <ToggleButton value={true}>Hábil</ToggleButton>
                    <ToggleButton value={false}>Inhábil</ToggleButton>
                  </ToggleButtonGroup>
                  {submitFailed && isBusinessHours === null && (
                    <Typography
                      sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#E7233B', mt: 0.625 }}
                    >
                      Selecciona si el envío es en horario hábil o inhábil
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </FormGrid>
        </FormSection>

        <TemplateComposer
          ref={composerRef}
          businessUnitId={businessUnitId}
          prefill={prefill}
          templateFilter={templateFilter}
          showSchedule
          ai={isRacAeropuerto ? null : { initial: 'enabled' }}
          allowTemplateCreation={!!currentUser?.allowTemplateCreation}
          createTemplateSlot={<CreateTemplateForm businessUnitId={businessUnitId} />}
          onCanSendChange={setTemplateCanSend}
          onSubmit={handleSubmit}
          panelMinHeight={500}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <BackButton onClick={handleClose} disabled={isCreating}>
          Cancelar
        </BackButton>
        <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
          {isCreating ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: NAVY }} /> Creando…
            </Box>
          ) : (
            'Crear'
          )}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewLeadModal;
